import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRole } from '../../utils/role';
import axiosInstance from '../../utils/axios';
import Wrapper from '../../components/Wrapper';
import { fetchPartners } from '../../store/features/partnersSlice';
import { uploadImageToS3, getFileExtension, deleteImageFromS3 } from '../../utils/s3Upload';

const ProductListPage = () => {
  const dispatch = useDispatch();
  const role = getRole();
  const isSuperAdmin = role === 'SUPPORT';

  // State
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [partnerFilter, setPartnerFilter] = useState('');
  const partners = useSelector((state) => state.partners?.partners ?? []);

  // Edit Modal State
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine base URL dynamically
  const getApiBaseUrl = () => {
    return isSuperAdmin ? '/super-admin/products' : '/admin/products';
  };

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = isSuperAdmin
        ? '/super-admin/products'
        : '/admin/products';

      const url = partnerFilter
        ? `${baseUrl}?partner_id=${partnerFilter}`
        : baseUrl;

      const { data } = await axiosInstance.get(url);
      setProducts(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin, partnerFilter]);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchPartners({ page: 1, limit: 500 }));
    }
  }, [dispatch, isSuperAdmin]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (previewUrl && !previewUrl.startsWith('http')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Edit Handlers
  const handleEditClick = (product) => {
    setEditingProduct(product);
    setEditForm({
      company: product.company || '',
      product_name: product.product_name || '',
      product_details: product.product_details || '',
      technical: product.technical || '',
      photo_url: product.photo_url || ''
    });
    setPreviewUrl(product.photo_url || '');
    setSelectedFile(null);
  };

  const closeEditModal = () => {
    setEditingProduct(null);
    setEditForm({});
    setPreviewUrl('');
    setSelectedFile(null);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let finalPhotoUrl = editForm.photo_url;

      if (selectedFile) {
        const fileExt = getFileExtension(selectedFile);
        finalPhotoUrl = await uploadImageToS3(selectedFile, fileExt, 'product-images', 'soildoctor-images');
      }

      const payload = {
        ...editForm,
        photo_url: finalPhotoUrl
      };

      await axiosInstance.patch(`${getApiBaseUrl()}/${editingProduct.product_id}`, payload);
      
      setProducts(products.map(p => 
        p.product_id === editingProduct.product_id ? { ...p, ...payload } : p
      ));
      
      closeEditModal();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    const productToDelete = products.find(p => p.product_id === productId);

    try {
      await axiosInstance.delete(`${getApiBaseUrl()}/${productId}`);
      
      if (productToDelete?.photo_url) {
        try {
          await deleteImageFromS3(productToDelete.photo_url);
        } catch (s3Error) {
          console.error('Database deletion succeeded, but S3 image deletion failed:', s3Error);
        }
      }
      setProducts(products.filter(p => p.product_id !== productId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete product');
    }
  };

  return (
    <Wrapper>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <h4 className="m-0 fw-bold">Product Catalog</h4>
        
        {isSuperAdmin && (
          <div className="d-flex align-items-center">
            <label className="me-2 fw-semibold text-secondary mb-0">Filter by Partner:</label>
            <select 
              className="form-select form-select-sm" 
              value={partnerFilter} 
              onChange={(e) => setPartnerFilter(e.target.value)}
              style={{ width: '250px' }}
            >
              <option value="">All Products</option>
              <option value="GLOBAL">Global / Unassigned</option>
              {partners.map(partner => (
                <option key={partner.admin_id} value={partner.admin_id}>
                  {partner.entity_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm border-0 mb-5">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4" style={{ width: '80px' }}>Image</th>
                  <th>Product Details</th>
                  <th>Technical Specs</th>
                  <th>Pack Sizes</th>
                  {isSuperAdmin && <th>Owner</th>}
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? "6" : "5"} className="text-center py-5 text-secondary">
                      <div className="spinner-border spinner-border-sm me-2"></div>Loading...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? "6" : "5"} className="text-center py-5 text-secondary">
                      No products found.
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.product_id}>
                      <td className="ps-4">
                        {product.photo_url ? (
                          <img src={product.photo_url} alt={product.product_name} className="rounded" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                        ) : (
                          <div className="bg-light rounded d-flex align-items-center justify-content-center text-secondary" style={{ width: '50px', height: '50px' }}>
                            <small>No Img</small>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-bold text-dark">{product.product_name}</div>
                        <div className="text-muted small">{product.company || '-'}</div>
                      </td>
                      <td>
                        <div className="small text-truncate" style={{ maxWidth: '250px' }} title={product.product_details}>
                          {product.product_details || '-'}
                        </div>
                        <div className="small text-muted text-truncate" style={{ maxWidth: '250px' }} title={product.technical}>
                          {product.technical || '-'}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {product.variants?.map((v, idx) => (
                            <span key={idx} className="badge bg-light text-dark border">{v.pack_size}</span>
                          ))}
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {product.admin_id ? (
                            <span className="badge bg-info text-dark">
                              {product.partner_name || 'Partner'}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">Global</span>
                          )}
                        </td>
                      )}
                      <td className="text-end pe-4">
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditClick(product)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(product.product_id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Inline Bootstrap Modal for Editing */}
      {editingProduct && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-light">
                  <h5 className="modal-title fw-bold">Edit Product</h5>
                  <button type="button" className="btn-close" onClick={closeEditModal}></button>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="modal-body">
                    
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Product Name <span className="text-danger">*</span></label>
                      <input type="text" name="product_name" className="form-control" value={editForm.product_name} onChange={handleEditInputChange} required />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Company Name</label>
                      <input type="text" name="company" className="form-control" value={editForm.company} onChange={handleEditInputChange} />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Product Details</label>
                      <textarea name="product_details" className="form-control" rows="2" value={editForm.product_details} onChange={handleEditInputChange}></textarea>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Technical Specs</label>
                      <textarea name="technical" className="form-control" rows="2" value={editForm.technical} onChange={handleEditInputChange}></textarea>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Update Image</label>
                      <div className="d-flex align-items-center gap-3">
                        {previewUrl && (
                          <img src={previewUrl} alt="Preview" className="rounded border" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                        )}
                        <input type="file" className="form-control form-control-sm" accept="image/*" onChange={handleImageChange} />
                      </div>
                    </div>

                    <div className="alert alert-warning py-2 mb-0 mt-3" style={{ fontSize: '0.85rem' }}>
                      <strong>Note:</strong> Pack sizes cannot be edited here currently. If pack sizes are wrong, please add a new product.
                    </div>

                  </div>
                  <div className="modal-footer bg-light border-top-0">
                    <button type="button" className="btn btn-secondary" onClick={closeEditModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default ProductListPage;